import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { AllowedGuard } from 'components/Tvendo/Common/UserAction';
import { useLocation, useNavigate } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardIcon from 'components/Card/CardIcon.js';
import CardHeader from 'components/Card/CardHeader.js';
import { cardTitle } from 'assets/jss/material-dashboard-pro-react.js';
import { Assignment, Ballot, Check, Close, HourglassEmpty } from '@material-ui/icons';
import Button from 'components/CustomButtons/Button.js';
import { Tooltip } from '@material-ui/core';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';

import { Toasty, useToasty } from 'hooks/Toasty';
import { WithApp } from 'contexts/appContext';
import { wsFindBy } from 'services/WService';
import { useModal } from 'hooks/useModal';
import ModalApprove from './CancelApprove/ModalApprove';

import VisibilityIcon from '@material-ui/icons/Visibility';
import { nPercent, currency } from 'hooks/Pipe';
import ModalFull from 'components/Tvendo/Common/ModalFull';
import OrdmOrderDetailShared from './OrdmOrderDetailShared';

import ConfirmCommited from './CancelApprove/ConfirmCommited';
import ConfirmRetained from './CancelApprove/ConfirmRetained';
import FilterCustom from 'components/Tvendo/Common/FilterCustom';
import { DataGrid, esES } from '@material-ui/data-grid';
import { wsPost } from 'services/WService';
import moment from 'moment';
import { useAuth } from 'hooks/useAuth';
import OrderTransactionModal from 'components/Tvendo/Order/OrderTransactionModal';
import PendientOrdersModal from 'components/Tvendo/Order/PendientOrdersModal';

const useStyles = makeStyles({
  cardIconTitle: {
    ...cardTitle,
    marginTop: '15px',
  },
});

export default WithApp(function Order(props) {
  const classes = useStyles();

  const [notify, showNotify] = useToasty(); //notificación despues de las
  const location = useLocation();
  const navigate = useNavigate();

  const { title, permission } = props;
  const [model, setOpen] = useModal(); //uso de las funciones del modal
  const [openDetails, setOpenDetails] = useState(false);
  const [idOrder, setIdOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [dataConfirm, setDataConfirm] = useState({ show: false });
  const [valueSearch, setValueSearch] = useState({});
  const [selectionModel, setSelectionModel] = useState([]);
  const [processing, setProcessing] = useState(false);
  const { hasPermission } = useAuth();
  const [logModel, setLogModalOpen] = useModal();
  const [pendientModel, setPendientModel] = useModal();
  const [dataLoading, setDataLoading] = useState(false);
  const [ruleOrders, setRuleOrders] = useState([]);
  const [ruleLoading, setRuleLoading] = useState(false);
  // Aprobados por regla: solo en móvil y con permiso de regla de descuento
  const canSeeRuleOrders = props.type === 'M' && hasPermission(permission.ruleIndex);

  const urlIsWeb = props.type === 'W' ? true : false;

  const columns = [
    {
      headerName: '#',
      field: 'num',
      width: 220,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="actions-right">
            {row.index + 1}
            <AllowedGuard permission={permission.show}>
              <Button
                justIcon
                round
                simple
                color="primary"
                disabled={processing}
                onClick={() => {
                  setOpenDetails(!openDetails);
                  setIdOrder(row.id);
                }}
              >
                <VisibilityIcon />
              </Button>
            </AllowedGuard>
            <AllowedGuard permission={permission.approve}>
              <Tooltip title="Aprobar">
                <Button
                  justIcon
                  round
                  simple
                  size="sm"
                  color="success"
                  disabled={processing}
                  onClick={() => {
                    setOpen(row);
                  }}
                >
                  <Check />
                </Button>
              </Tooltip>
            </AllowedGuard>
            <AllowedGuard permission={permission.annular}>
              <Tooltip title="Anular">
                <Button
                  justIcon
                  round
                  simple
                  color="danger"
                  disabled={processing}
                  onClick={() => {
                    setDataConfirm({
                      show: true,
                      idOrder: row.id,
                      ObjType: row.ObjType,
                      message: 'Anular',
                      action: 'N',
                    });
                  }}
                >
                  <Close />
                </Button>
              </Tooltip>
            </AllowedGuard>
            <Tooltip title="Logs">
              <Button
                justIcon
                round
                simple
                color="warning"
                disabled={processing}
                onClick={() => {
                  setLogModalOpen(row);
                }}
              >
                <Ballot />
              </Button>
            </Tooltip>
            <Tooltip title="Pendientes de pago">
              <Button
                justIcon
                round
                simple
                color="info"
                disabled={processing}
                onClick={() => {
                  setPendientModel(row);
                }}
              >
                <HourglassEmpty />
              </Button>
            </Tooltip>
          </div>
        );
      },
      valueGetter: ({ row }) => row.index + 1,
    },
    {
      headerName: 'Fecha',
      field: 'date',
      width: 150,
      sortable: false,
      renderCell: ({ row }) => {
        return moment(row.created_at).format('DD/MM/YYYY HH:mm');
      },
      valueGetter: ({ row }) => moment(row.created_at).format('DD/MM/YYYY HH:mm'),
    },
    {
      headerName: 'Tipo Doc',
      field: 'ObjType',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        return row.ObjType === '14' ? 'Nota de crédito' : 'Orden';
      },
    },
    {
      headerName: 'Tipo',
      field: 'order_type',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        return row.order_type === 'M'
          ? 'Móvil'
          : row.order_type === 'W'
          ? 'Web'
          : row.order_type === 'O'
          ? 'Oficina'
          : 'No definido';
      },
    },
    {
      headerName: 'Zona',
      field: 'sede',
      width: 140,
      sortable: false,
      renderCell: ({ row }) => {
        return `${row.base_zone_code} - ${row.base_zone_description}`;
      },
      valueGetter: ({ row }) => `${row.base_zone_code} - ${row.base_zone_description}`,
    },
    {
      headerName: 'Vendedor',
      field: 'seller',
      sortable: false,
      renderCell: ({ row }) => row.SlpName,
      flex: 1,
      minWidth: 250,
      valueGetter: ({ row }) => row.SlpName,
    },
    {
      headerName: 'Pedido',
      field: 'order',
      width: 120,
      renderCell: ({ row }) => row.id.toString(),
      valueGetter: ({ row }) => row.id.toString(),
    },
    {
      headerName: 'Cliente',
      field: 'customer',
      width: 120,
      sortable: false,
      renderCell: ({ row }) => row.CardCode,
      valueGetter: ({ row }) => row.CardCode,
    },
    {
      headerName: 'Razón social',
      field: 'rSocial',
      renderCell: ({ row }) => row.CardName,
      valueGetter: ({ row }) => row.CardName,
      flex: 1,
      sortable: false,
      minWidth: 250,
    },
    {
      headerName: 'Saldo',
      field: 'saldo',
      width: 80,
      renderCell: ({ row }) => row.balance_sap,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      headerName: 'Término pago',
      field: 'paymentTerm',
      sortable: false,
      width: 130,
      renderCell: ({ row }) => row.PymntGroup,
      valueGetter: ({ row }) => row.PymntGroup,
    },
    {
      headerName: 'Importe',
      field: 'amount',
      width: 150,
      renderCell: ({ row }) => currency(row.total_amount),
      valueGetter: ({ row }) => currency(row.total_amount),
    },
    {
      headerName: 'Margen',
      field: 'margen',
      sortable: false,
      width: 100,
      renderCell: ({ row }) => currency(row.margin_amount),
      valueGetter: ({ row }) => currency(row.margin_amount),
    },
    {
      headerName: '% Desc',
      field: 'digitate_discount',
      sortable: false,
      disableColumnMenu: true,
      width: 80,
      renderCell: ({ row }) => nPercent(row.digitate_discount),
      valueGetter: ({ row }) => nPercent(row.digitate_discount),
    },
    {
      headerName: 'Estado',
      field: 'state',
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        row.state === 'R'
          ? 'Retenido'
          : row.state === 'X'
          ? 'Normal'
          : row.state === 'S'
          ? 'Sin Stock'
          : 'No definido',
    },
    {
      headerName: 'Comentario',
      field: 'comments',
      sortable: false,
      width: 200,
      renderCell: ({ row }) => row.comment,
      valueGetter: ({ row }) => row.comment,
    },
    {
      headerName: 'Retención',
      field: 'retention_reasons',
      width: 250,
      sortable: false,
      renderCell: ({ row }) => row.retention_reasons || 'No',
      valueGetter: ({ row }) => row.retention_reasons || 'No',
    },
  ];

  const ruleColumns = [
    {
      headerName: '#',
      field: 'num',
      width: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="actions-right">
          {row.index + 1}
          <AllowedGuard permission={permission.ruleShow}>
            <Button
              justIcon
              round
              simple
              color="primary"
              onClick={() => {
                setOpenDetails(!openDetails);
                setIdOrder(row.id);
              }}
            >
              <VisibilityIcon />
            </Button>
          </AllowedGuard>
        </div>
      ),
      valueGetter: ({ row }) => row.index + 1,
    },
    {
      headerName: 'Fecha',
      field: 'date',
      width: 150,
      sortable: false,
      renderCell: ({ row }) => moment(row.created_at).format('DD/MM/YYYY HH:mm'),
      valueGetter: ({ row }) => moment(row.created_at).format('DD/MM/YYYY HH:mm'),
    },
    {
      headerName: 'Vendedor',
      field: 'seller',
      sortable: false,
      renderCell: ({ row }) => row.SlpName,
      flex: 1,
      minWidth: 200,
      valueGetter: ({ row }) => row.SlpName,
    },
    {
      headerName: 'Pedido',
      field: 'order',
      width: 100,
      renderCell: ({ row }) => row.id.toString(),
      valueGetter: ({ row }) => row.id.toString(),
    },
    {
      headerName: 'Cliente',
      field: 'customer',
      width: 110,
      sortable: false,
      renderCell: ({ row }) => row.CardCode,
      valueGetter: ({ row }) => row.CardCode,
    },
    {
      headerName: 'Razón social',
      field: 'rSocial',
      renderCell: ({ row }) => row.CardName,
      valueGetter: ({ row }) => row.CardName,
      flex: 1,
      sortable: false,
      minWidth: 220,
    },
    {
      headerName: 'Importe',
      field: 'amount',
      width: 130,
      renderCell: ({ row }) => currency(row.total_amount),
      valueGetter: ({ row }) => currency(row.total_amount),
    },
    {
      headerName: 'Comentario',
      field: 'comments',
      sortable: false,
      width: 200,
      renderCell: ({ row }) => row.comment,
      valueGetter: ({ row }) => row.comment,
    },
  ];

  async function anulateMultiple() {
    setDataLoading(true);
    setProcessing(true);
    let res = await wsPost('ordm/order/approve-multiple', {
      orders: selectionModel,
    });
    setProcessing(false);
    setDataLoading(false);
    if (res.success) {
      showNotify(res.message);
      listOrders();
    } else {
      showNotify(res.message, 'error');
    }
  }

  async function listOrders() {
    setSelectionModel([]);
    let params = {
      is_order: 'Y',
      state: props.type === 'C' ? 'X' : 'R',
      order_type: urlIsWeb ? (props.type === 'C' ? ['W', 'O'] : ['W', 'O', 'S']) : valueSearch.state ? valueSearch.state : 'M',
      start_date: valueSearch.start_date,
      end_date: valueSearch.end_date,
    };

    if (urlIsWeb) {
      params.with_credit_notes = 'Y';
    }

    if (valueSearch.base_zone_id !== undefined) {
      params.base_zone_id = valueSearch.base_zone_id;
    }
    setDataLoading(true);
    let response = await wsFindBy('ordm/order', params);
    setDataLoading(false);
    if (response.success) {
      setOrders(
        response.data.map((order, index) => {
          order.index = index;
          order.multi = false;
          if (typeof order.comment !== 'string') return order;
          if (order.comment.split(',').length === 1) {
            let str = order.comment
              .toUpperCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '');
            order.multi = str.includes('MAYORISTA');
          }
          order.hasVencidas = false;
          if (typeof order.retention_reasons === 'string') {
            const retentionStr = order.retention_reasons
              .toUpperCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '');
            order.hasVencidas = retentionStr.includes('VENCIDAS');
          }
          return order;
        }),
      );
    }
  }

  async function listRuleOrders() {
    if (!canSeeRuleOrders) return;
    setRuleLoading(true);
    const response = await wsFindBy('ordm/order/approved-by-rule', {});
    setRuleLoading(false);
    if (response.success) {
      setRuleOrders(response.data.map((order, index) => ({ ...order, index })));
    }
  }

  useEffect(() => {
    if (props.type === 'C') {
      if (valueSearch.state) listOrders();
    } else {
      listOrders();
    }
  }, [valueSearch]);

  useEffect(() => {
    listRuleOrders();
  }, []);

  useEffect(() => {
    if (openDetails) {
      navigate(`${location.pathname}#${idOrder}`, { replace: true });
    }
  }, [openDetails]);

  useEffect(() => {
    if (location.hash !== '') {
      setOpenDetails(true);
      setIdOrder(location.hash.substring(1));
    }
  }, []);

  function setOrder(order) {
    let index = orders.findIndex((item) => item.id === order.id);
    if (index !== -1) {
      setOrders((orders) => {
        let newOrders = [...orders];
        newOrders[index] = { ...newOrders[index], ...order };
        return newOrders;
      });
      let newOrder = { ...orders[index], ...order };
      setOpen(newOrder);
    }
  }

  const approveUrl = useMemo(() => {
    if (props.type === 'R') return 'approve-retained';
    if (props.type === 'C') return 'approve-commited';
    return 'approve';
  }, [props.type, model]);

  return (
    <Fragment>
      <ModalApprove
        model={model}
        setOpen={setOpen}
        showNotify={showNotify}
        reload={listOrders}
        approveUrl={approveUrl}
        setOrder={setOrder}
      />
      <OrderTransactionModal model={logModel} setOpen={setLogModalOpen} entity="ordm/order" />
      <PendientOrdersModal model={pendientModel} setOpen={setPendientModel} />
      <Toasty action={notify} />

      {props.type === 'C' ? (
        <ConfirmCommited
          {...dataConfirm}
          showNotify={showNotify}
          reload={listOrders}
          close={() => setDataConfirm({ show: false })}
        />
      ) : (
        <ConfirmRetained
          {...dataConfirm}
          showNotify={showNotify}
          reload={listOrders}
          close={() => setDataConfirm({ show: false })}
        />
      )}

      {openDetails ? (
        <ModalFull
          title="Detalles"
          open={openDetails}
          setOpen={(openDetails) => {
            setOpenDetails(openDetails);
            navigate(`${location.pathname}`, { replace: true });
          }}
        >
          <OrdmOrderDetailShared idOrder={idOrder} />
        </ModalFull>
      ) : undefined}
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader icon>
              <CardIcon color={props.themeContext.buttonColor}>
                <Assignment />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                Pedidos {title} {dataLoading && <LoadingSpinner />}
                <AllowedGuard permission={permission.approve}>
                  {['M', 'W'].includes(props.type) && (
                    <>
                      <Button
                        color="success"
                        onClick={anulateMultiple}
                        disabled={processing || !selectionModel.length > 0}
                        style={{ marginLeft: '20px' }}
                      >
                        Aprobar seleccionados
                      </Button>

                      {processing && <div className="lds-dual-ring"></div>}
                    </>
                  )}
                </AllowedGuard>
              </h4>
            </CardHeader>
            <CardBody>
              {props.type === 'C' ? (
                <FilterCustom
                  zone
                  getFilter={setValueSearch}
                  state
                  optionValues={['W', 'M', 'O']}
                  nameOptions={['Web', 'Móvil', 'Oficina']}
                  defaultState="M"
                  dateRange
                  diffRange={7}
                />
              ) : undefined}

              <div style={{ height: '700px' }}>
                <DataGrid
                  checkboxSelection={
                    !hasPermission(permission.approve)
                      ? false
                      : processing === true
                      ? false
                      : ['M', 'W'].includes(props.type)
                  }
                  selectionModel={selectionModel}
                  disableSelectionOnClick
                  isRowSelectable={({ row }) => {
                    if (!hasPermission(permission.approve)) return false;
                    if (row.digitate_discount > 0) return false;
                    if (row.hasVencidas) return false;
                    return row.multi === true;
                  }}
                  onSelectionModelChange={(model) => {
                    if (processing) return;
                    setSelectionModel(model);
                  }}
                  filterMode="client"
                  localeText={esES.props.MuiDataGrid.localeText}
                  getRowId={(row) => row.id}
                  disableColumnReorder
                  columns={columns}
                  rows={orders}
                />
              </div>
            </CardBody>
          </Card>
        </GridItem>
        {canSeeRuleOrders && (
          <GridItem xs={12}>
            <Card>
              <CardHeader icon>
                <CardIcon color={props.themeContext.buttonColor}>
                  <Assignment />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>
                  Pedidos aprobados por regla (hoy) {ruleLoading && <LoadingSpinner />}
                </h4>
              </CardHeader>
              <CardBody>
                <div style={{ height: '500px' }}>
                  <DataGrid
                    filterMode="client"
                    localeText={esES.props.MuiDataGrid.localeText}
                    getRowId={(row) => row.id}
                    disableColumnReorder
                    columns={ruleColumns}
                    rows={ruleOrders}
                  />
                </div>
              </CardBody>
            </Card>
          </GridItem>
        )}
      </GridContainer>
    </Fragment>
  );
});
